/**
 * Column resize utilities for table columns
 * Handles resize logic, persistence, and restoration
 */

const STORAGE_KEY = "tableColumnWidths";
const MIN_COLUMN_WIDTH = 80; // Minimum width in pixels for better readability
const MAX_COLUMN_WIDTH = 500; // Maximum width in pixels

/**
 * Save column widths to localStorage
 * @param {Object} columnWidths - Object with column keys and width values
 */
export function saveColumnWidths(columnWidths) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths));
  } catch (error) {
    console.error("Failed to save column widths:", error);
  }
}

/**
 * Load saved column widths from localStorage
 * @returns {Object|null} Saved column widths or null if not found
 */
export function loadColumnWidths() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Failed to load column widths:", error);
    return null;
  }
}

/**
 * Clear saved column widths
 */
export function clearColumnWidths() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear column widths:", error);
  }
}

/**
 * Merge saved widths with default widths
 * @param {Object} defaultWidths - Default column widths from config
 * @param {Object} savedWidths - Saved column widths from localStorage
 * @returns {Object} Merged column widths
 */
export function mergeColumnWidths(defaultWidths, savedWidths) {
  if (!savedWidths) return defaultWidths;

  // Start with defaults and override with saved values
  const merged = { ...defaultWidths };

  Object.keys(savedWidths).forEach((key) => {
    if (key in merged) {
      // Validate the saved width
      const width = parseInt(savedWidths[key], 10);
      if (
        !isNaN(width) &&
        width >= MIN_COLUMN_WIDTH &&
        width <= MAX_COLUMN_WIDTH
      ) {
        merged[key] = `${width}px`;
      }
    }
  });

  return merged;
}

/**
 * Convert pixel width to number
 * @param {string} width - Width string like '150px'
 * @returns {number} Width as number
 */
export function parseWidth(width) {
  if (typeof width === "number") return width;
  const parsed = parseInt(width, 10);
  return isNaN(parsed) ? 150 : parsed;
}

/**
 * Constrain width within min/max bounds
 * @param {number} width - Proposed width
 * @returns {number} Constrained width
 */
export function constrainWidth(width) {
  return Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, width));
}

/**
 * Calculate auto width based on content
 * @param {string} columnKey - Column identifier
 * @param {Array} data - Table data
 * @param {Object} columnDef - Column definition
 * @returns {number} Calculated width
 */
export function calculateAutoWidth(columnKey, data, columnDef) {
  // Base width on column label with balanced padding
  const labelPadding = 28; // Comfortable padding for headers
  let maxWidth = columnDef.label
    ? columnDef.label.length * 8 + labelPadding
    : 100;

  // Sample data to determine content width
  const sampleSize = Math.min(data.length, 50);
  for (let i = 0; i < sampleSize; i++) {
    const value = data[i][columnKey];
    if (value) {
      const contentLength = String(value).length;
      // Balanced padding for readability
      const charWidth = 7.5; // Slightly wider for better readability
      const contentPadding = 24; // Comfortable padding
      const estimatedWidth = contentLength * charWidth + contentPadding;
      maxWidth = Math.max(maxWidth, estimatedWidth);
    }
  }

  return constrainWidth(maxWidth);
}

/**
 * Create resize handle element
 * @returns {HTMLElement} Resize handle div
 */
export function createResizeHandle() {
  const handle = document.createElement("div");
  handle.className = "column-resize-handle";
  handle.innerHTML = '<div class="resize-line"></div>';
  return handle;
}

/**
 * Initialize column resize functionality
 * @param {HTMLElement} tableContainer - Table container element
 * @param {Function} onResize - Callback when column is resized
 */
export function initializeColumnResize(tableContainer, onResize) {
  let isResizing = false;
  let currentColumn = null;
  let startX = 0;
  let startWidth = 0;

  // Create resize indicator if it doesn't exist
  let indicator = document.getElementById("resize-indicator");
  if (!indicator) {
    indicator = document.createElement("div");
    indicator.id = "resize-indicator";
    document.body.appendChild(indicator);
  }

  // Mouse/Touch down handler
  const handleMouseDown = (e) => {
    const target = e.target.closest(".column-resize-handle");
    if (!target) return;

    isResizing = true;
    // Get column from parent element's data attribute - support multiple class names
    const columnHeader = target.closest(
      ".column-header, .contacts-column-header, th[data-column]",
    );
    currentColumn = columnHeader
      ? columnHeader.dataset.column
      : target.dataset.column;

    if (!currentColumn) {
      console.warn("No column data found for resize handle");
      return;
    }

    // Support both mouse and touch events
    // Use pageX for calculations (accounts for scroll)
    startX = e.type.includes("touch") ? e.touches[0].pageX : e.pageX;

    // For grid-based layouts, get the parent header element
    const columnElement = columnHeader || target.parentElement;
    startWidth = columnElement.offsetWidth;

    // Add resizing class to disable hover effects
    tableContainer.classList.add("resizing");

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    e.preventDefault();
  };

  // Mouse/Touch move handler
  const handleMouseMove = (e) => {
    if (!isResizing) return;

    // Support both mouse and touch events
    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const pageX = e.type.includes("touch") ? e.touches[0].pageX : e.pageX;
    const diff = pageX - startX;
    constrainWidth(startWidth + diff);

    // Show resize indicator at correct position
    const indicator = document.getElementById("resize-indicator");
    if (indicator) {
      // Use clientX for fixed positioning (relative to viewport)
      indicator.style.left = `${clientX}px`;
      indicator.style.display = "block";
    }

    e.preventDefault();
  };

  // Mouse/Touch up handler
  const handleMouseUp = (e) => {
    if (!isResizing) return;

    isResizing = false;

    // Support both mouse and touch events
    const pageX = e.type.includes("touch")
      ? e.changedTouches[0].pageX
      : e.pageX;
    const diff = pageX - startX;
    const finalWidth = constrainWidth(startWidth + diff);

    // Hide resize indicator
    const indicator = document.getElementById("resize-indicator");
    if (indicator) {
      indicator.style.display = "none";
    }

    // Remove resizing class
    tableContainer.classList.remove("resizing");

    // Call resize callback
    if (onResize && currentColumn) {
      onResize(currentColumn, finalWidth);
    }

    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    currentColumn = null;
  };

  // Attach event listeners - both mouse and touch
  tableContainer.addEventListener("mousedown", handleMouseDown);
  tableContainer.addEventListener("touchstart", handleMouseDown, {
    passive: false,
  });
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("touchmove", handleMouseMove, { passive: false });
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("touchend", handleMouseUp);

  // Cleanup function
  return () => {
    tableContainer.removeEventListener("mousedown", handleMouseDown);
    tableContainer.removeEventListener("touchstart", handleMouseDown);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("touchmove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("touchend", handleMouseUp);
  };
}

const columnResizeUtils = {
  saveColumnWidths,
  loadColumnWidths,
  clearColumnWidths,
  mergeColumnWidths,
  parseWidth,
  constrainWidth,
  calculateAutoWidth,
  createResizeHandle,
  initializeColumnResize,
};

export default columnResizeUtils;
